/* ---------------- ABCONVERT PRICE TEST ----------------
   The price displayed on product cards is owned by this script (it rewrites
   `.main-price` from the active size-pill's `data-price`). ABConvert's own
   storefront script can't override that, so we ask its public API
   (window.abconvert.price) for the assigned test price and render it ourselves.
   Falls back to the original `data-price` whenever no test price applies.        */

// True only when the merchant setting is on AND the ABConvert app is present.
function abcSyncEnabled() {
  return window.ABCONVERT_PRICE_SYNC_ENABLED !== false &&
         !!(window.abconvert && window.abconvert.price);
}

// The test group this visitor is assigned to for the given product, or null.
function abcAssignedGroup(productId) {
  const dict = window.abconvertPriceCartTransformTestDict;
  const entry = dict && dict[productId];
  if (!entry) return null;
  let groupIndex;
  try {
    const groups = JSON.parse(localStorage.getItem('abconvert-experiment-to-group-index') || '{}');
    groupIndex = groups ? groups[entry.experimentId] : undefined;
  } catch (e) {}
  if ((groupIndex === undefined || groupIndex === null) &&
      window.abconvert && window.abconvert.user && window.abconvert.user.getAssignedGroup) {
    const g = window.abconvert.user.getAssignedGroup(entry.experimentId);
    groupIndex = (g && typeof g === 'object') ? g.groupIndex : g;
  }
  return (groupIndex === undefined || groupIndex === null) ? null : groupIndex;
}

// Price for a specific group/variant. Market-specific first; base price (e.g. DE)
// has no countryMap entry and getByVariant returns null for an explicit country
// not in countryMap, so we retry without a country to get the base price.
function abcGetByVariantGroup(variantId, groupIndex) {
  const api = window.abconvert && window.abconvert.price;
  if (!api || groupIndex === null || groupIndex === undefined) return null;
  const country = (window.Shopify && window.Shopify.country) || '';
  let res = country ? api.getByVariant(variantId, groupIndex, { country }) : null;
  if (!res) res = api.getByVariant(variantId, groupIndex);
  return res || null;
}

function getAbcTestPrice(productId, variantId) {
  try {
    if (!abcSyncEnabled()) return null;
    const dict = window.abconvertPriceCartTransformTestDict;
    if (!dict || !productId || !variantId || !dict[productId]) return null;
    const groupIndex = abcAssignedGroup(productId);
    if (groupIndex === null) return null;
    return abcGetByVariantGroup(variantId, groupIndex);
  } catch (e) {
    return null;
  }
}

/* Parse a localized money string ("77,88€", "1.234,56 €", "$1,234.56") to a number. */
function parseMoney(str) {
  if (str == null) return NaN;
  const t = String(str).replace(/[^\d.,]/g, '');
  if (!t) return NaN;
  const dec = t.match(/([.,])(\d{1,2})$/);
  let norm;
  if (dec) {
    const sep = dec[1];
    const thou = sep === ',' ? '.' : ',';
    norm = t.split(thou).join('').replace(sep, '.');
  } else {
    norm = t.replace(/[.,]/g, '');
  }
  return parseFloat(norm);
}

/* Reformat a numeric amount to match an already-rendered money string
   (keeps the market's currency symbol, position and decimal/thousand separators). */
function formatLikeTemplate(template, amount) {
  const n = Number(amount);
  if (template == null || !isFinite(n)) return template;
  const t = String(template);
  const numMatch = t.match(/[\d.,]+/);
  if (!numMatch) return t;

  const numToken = numMatch[0];
  const decMatch = numToken.match(/([.,])(\d{1,2})$/);
  const decSep = decMatch ? decMatch[1] : '.';
  const thouSep = decSep === ',' ? '.' : ',';

  const abs = Math.abs(n);
  let intPart = Math.floor(abs).toString().replace(/\B(?=(\d{3})+(?!\d))/g, thouSep);
  const frac = Math.round((abs - Math.floor(abs)) * 100).toString().padStart(2, '0');
  const newNum = (n < 0 ? '-' : '') + intPart + decSep + frac;

  return t.replace(numToken, newNum);
}

// Resolve the badge colour locally (no dependency on AbcUI, so the FIRST render
// already gets the right colour and there's no red→green flash):
// group-name keyword → index fallback (this experiment: group 2 = green).
function isCardPickerMode() {
  return document.documentElement.classList.contains('card-picker-ab-b');
}

function applyCardPickerAssignment() {
  const cfg = window.CARD_PICKER_ABTEST;
  if (!cfg || !cfg.enabled) return;
  if (cfg.force || /(?:^|[?&])card_picker=1(?:&|$)/.test(location.search)) {
    document.documentElement.classList.add('card-picker-ab-b');
    return;
  }
  const resolve = window.resolveCardPickerGroup;
  const groupIndex = resolve ? resolve(cfg) : null;
  if (Number(groupIndex) === Number(cfg.variantGroup)) {
    document.documentElement.classList.add('card-picker-ab-b');
  }
}

function setAtcVisualState(btn, state) {
  if (!btn) return;
  btn.classList.toggle('is-loading', state === 'loading');
  btn.classList.toggle('has-success', state === 'success');
  btn.disabled = state === 'loading';
}

async function addVariantToCart(variantId, btn, cardIcon) {
  if (!variantId) return false;
  setAtcVisualState(btn, 'loading');
  if (cardIcon && cardIcon !== btn) setAtcVisualState(cardIcon, 'loading');

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    });

    if (!res.ok) throw new Error('Add to cart failed');
    await res.json();

    const langPrefix = window.Shopify?.routes?.root || '';
    const sectionsRes = await fetch(
      `${langPrefix}cart?sections=cart-drawer`.replace('//', '/')
    );
    const sections = await sectionsRes.json();

    setAtcVisualState(btn, 'success');
    if (cardIcon) setAtcVisualState(cardIcon, 'success');

    await new Promise((resolve) => setTimeout(resolve, 400));

    const dialog = document.getElementById('CardVariantPicker');
    if (dialog && dialog.open) dialog.close();

    if (window.eventBus) {
      window.eventBus.emit('update:cart:drawer', {
        sections: { 'cart-drawer': sections['cart-drawer'] }
      });
      window.eventBus.emit('open:cart:drawer', { scrollToTop: true });
    }
    const cartRes = await fetch('/cart.js');
    const cart = await cartRes.json();
    window.wetheme?.updateCartCount(cart);

    setTimeout(() => {
      setAtcVisualState(btn, 'idle');
      if (cardIcon) setAtcVisualState(cardIcon, 'idle');
    }, 1600);
    return true;
  } catch (err) {
    console.error(err);
    setAtcVisualState(btn, 'idle');
    if (cardIcon) setAtcVisualState(cardIcon, 'idle');
    return false;
  }
}

function abcResolveColorLocal(productId, abcPrice) {
  const s = String((abcPrice && abcPrice.groupName) || '').toLowerCase();
  if (s.indexOf('green') >= 0 || s.indexOf('grün') >= 0 || s.indexOf('grun') >= 0) return 'green';
  if (s.indexOf('red') >= 0 || s.indexOf('rot') >= 0) return 'red';
  return Number(abcAssignedGroup(productId)) === 2 ? 'green' : 'red';
}

function getParentCardSwiper(el) {
  const slider = el && el.closest('grid-slider');
  return slider && slider.swiper ? slider.swiper : null;
}

function isolatePillStripFromCarousel(strip) {
  if (!strip || strip.dataset.carouselIsolated === '1') return;
  strip.dataset.carouselIsolated = '1';
  strip.classList.add('swiper-no-swiping');
  const host = strip.closest('.variant-selector-wrapper') || strip;
  host.classList.add('swiper-no-swiping');

  let dragging = false;
  let startX = 0;
  let startScroll = 0;
  let moved = false;

  const onMove = (event) => {
    if (!dragging) return;
    event.stopPropagation();
    const dx = event.clientX - startX;
    if (Math.abs(dx) > 4) moved = true;
    if (!moved) return;
    strip.scrollLeft = startScroll - dx;
    event.preventDefault();
  };

  const onEnd = () => {
    if (!dragging) return;
    dragging = false;
    document.removeEventListener('pointermove', onMove, true);
    document.removeEventListener('pointerup', onEnd, true);
    document.removeEventListener('pointercancel', onEnd, true);
    const swiper = getParentCardSwiper(strip);
    if (swiper) swiper.allowTouchMove = true;
  };

  host.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const swiper = getParentCardSwiper(strip);
    if (swiper) swiper.allowTouchMove = false;
    dragging = true;
    moved = false;
    startX = event.clientX;
    startScroll = strip.scrollLeft;
    document.addEventListener('pointermove', onMove, { capture: true, passive: false });
    document.addEventListener('pointerup', onEnd, { capture: true });
    document.addEventListener('pointercancel', onEnd, { capture: true });
  }, true);
}

function armSliderPillNoSwipe(el) {
  const swiper = el && el.swiper;
  if (!swiper || el.dataset.pillsArmed === '1') return;
  el.dataset.pillsArmed = '1';
  swiper.params.noSwiping = true;
  swiper.params.noSwipingSelector = '.variant-selector, .variant-selector-wrapper, .swiper-no-swiping';
}

function initVariantChangeForProductCard(context = document) {

  context.querySelectorAll('product-card').forEach(function(productCard) {

    if (productCard.dataset.variantChangeInit === '1') return;
    productCard.dataset.variantChangeInit = '1';

    const productId = productCard.dataset.productId;
    const pills = productCard.querySelectorAll('.size-pill[data-variant-id]');
    const variantInput = productCard.querySelector(`input[type="hidden"].variant-input-${productId}`);
    const form = productCard.querySelector(`#InlineAddToCartForm-${productId}`);
    const mainImageLink = productCard.querySelector('a.grid__image');
    const variantSelector = productCard.querySelector('.variant-selector.size-selector');
    const stockBox = productCard.querySelector('.variant-stock-alert');
    const stockQty = productCard.querySelector('.stock-quantity');
    isolatePillStripFromCarousel(variantSelector);

    /* ---------------- IMAGE ---------------- */

    function updateMainImage(imageSrc) {
      if (!mainImageLink) return;
      const img = mainImageLink.querySelector('img');
      if (img && imageSrc) {
        img.src = imageSrc;
        img.srcset = "";
      }
    }

    /* ---------------- ACTIVATE ---------------- */

function activatePill(pill) {

  if (!pill || pill.classList.contains('disabled')) return;

  productCard.querySelectorAll('.size-pill')
    .forEach(p => p.classList.remove('active'));

  pill.classList.add('active');

  /* ---------------- VARIANT ---------------- */

  const variantId = pill.getAttribute('data-variant-id');
  if (variantInput) variantInput.value = variantId;

  /* ---------------- IMAGE ---------------- */

  const variantImage = pill.getAttribute('data-variant-image');
  if (variantImage) updateMainImage(variantImage);

  /* ---------------- PRICE ---------------- */

  let pricePill = pill;
  let fromPrefix = '';
  if (isCardPickerMode() && pills.length > 1) {
    let cheapest = null;
    let cheapestVal = Infinity;
    let dearestVal = -Infinity;
    pills.forEach((p) => {
      const val = parseMoney(p.getAttribute('data-price'));
      if (!(val > 0)) return;
      if (val < cheapestVal) {
        cheapestVal = val;
        cheapest = p;
      }
      if (val > dearestVal) dearestVal = val;
    });
    if (cheapest && cheapestVal < dearestVal) {
      pricePill = cheapest;
      const fromText = (window.CARD_PICKER_ABTEST && window.CARD_PICKER_ABTEST.fromText) || '';
      if (fromText) fromPrefix = `<span class="price--from-text">${fromText}</span> `;
    }
  }

  const origPrice = pricePill.getAttribute('data-price');
  const origCompareAtPrice = pricePill.getAttribute('data-compare-at-price');

  // Use ABConvert's assigned test price when this variant is under test,
  // formatted to match the theme's money format for the current market.
  let price = origPrice;
  let compareAtPrice = origCompareAtPrice;

  const abcPrice = getAbcTestPrice(productId, pricePill.getAttribute('data-variant-id'));
  let abcIsNew = false;
  let abcColor = 'red';
  if (abcPrice && abcPrice.price != null && abcPrice.price !== '') {
    price = formatLikeTemplate(origPrice, abcPrice.price);
    const control = abcGetByVariantGroup(pricePill.getAttribute('data-variant-id'), 0);
    abcIsNew = !!(control && control.price != null && Number(abcPrice.price) !== Number(control.price));
    if (abcIsNew) {
      abcColor = abcResolveColorLocal(productId, abcPrice);
      // Always show the original (control) price struck-through next to the test price.
      compareAtPrice = formatLikeTemplate(origPrice, control.price);
    } else {
      compareAtPrice = (abcPrice.compareAtPrice != null && abcPrice.compareAtPrice !== '')
        ? formatLikeTemplate(origPrice, abcPrice.compareAtPrice)
        : '';
    }
  }

  // Metafield-driven old-price label (independent of the ABConvert test).
  // Uses data-old-price rendered server-side; shows the current price in red
  // with the struck old price + "New price" pill.
  let oldPriceMode = false;
  if (window.OLD_PRICE_LABEL_ENABLED) {
    const oldP = pricePill.getAttribute('data-old-price');
    if (oldP && oldP !== '') {
      oldPriceMode = true;
      abcIsNew = true;   // reuse the red "new price" price styling
      abcColor = 'red';
      compareAtPrice = oldP;
    }
  }

  const priceContainer = productCard.querySelector('.product-grid--price');
  const mainPrice = priceContainer?.querySelector('.main-price');
  // Direct child only: product-grid-item-price.liquid also renders a .unit-price
  // inside .main-price (the Grundpreis fallback for cards without pills), and that
  // one gets wiped by the innerHTML assignment below.
  const unitPriceEl = priceContainer?.querySelector(':scope > .unit-price');

  if (mainPrice) {

    const priceClass = abcIsNew ? 'igPrice abc-price--new' : 'igPrice';
    const baseColor = (compareAtPrice && compareAtPrice !== price) ? '#FF6F00' : '#587D3E';

    // New price first, then struck original (matches design).
    const priceVariantId = pricePill.getAttribute('data-variant-id') || variantId;
    const hasOld = !!(compareAtPrice && (oldPriceMode || compareAtPrice !== price));
    // Screen-reader labels (translated, from <body data-text-*>): struck price = regular, current = sale.
    const srLabel = (key) => {
      const text = document.body.getAttribute(`data-text-${key}`);
      return text ? `<span class="visually-hidden">${text}</span>` : '';
    };
    const newSpan = `${hasOld ? srLabel('sale-price') : ''}<span class="${priceClass}" style="color:${baseColor}; font-weight:bold;" data-variant-id="${priceVariantId}">${price}</span>`;
    const oldSpan = hasOld
      ? `${srLabel('regular-price')}<span class="igComparePrice abc-orig-price" style="text-decoration:line-through; color:#888; margin-left:6px;" data-variant-id="${priceVariantId}">${compareAtPrice}</span>`
      : '';
    mainPrice.innerHTML = `${fromPrefix}${newSpan}${oldSpan}`;

    // "New price" pill as its own row in the price container. CSS orders the
    // container as: prices → pill → unit price (grid = pill only, no promo row).
    let pill = '';
    if (oldPriceMode) {
      const txt = window.OLD_PRICE_LABEL_TEXT || '';
      if (txt) pill = `<span class="abc-new-price-label abc--red"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M21.4 11.6l-9-9A2 2 0 0 0 11 2H4a2 2 0 0 0-2 2v7c0 .5.2 1 .6 1.4l9 9c.8.8 2 .8 2.8 0l7-7c.8-.8.8-2 0-2.8zM6.5 7A1.5 1.5 0 1 1 8 5.5 1.5 1.5 0 0 1 6.5 7z"/></svg><span>${txt}</span></span>`;
    } else if (abcIsNew && (window.ABCONVERT_LABELS || window.ABCONVERT_NEW_PRICE_LABEL)) {
      if (window.AbcUI && window.AbcUI.pillHtml) {
        pill = window.AbcUI.pillHtml(abcColor, false);
      } else {
        const txt = (window.ABCONVERT_LABELS && window.ABCONVERT_LABELS.newPrice) || window.ABCONVERT_NEW_PRICE_LABEL || '';
        if (txt) pill = `<span class="abc-new-price-label abc--${abcColor}">${txt}</span>`;
      }
    }
    if (priceContainer) {
      priceContainer.classList.toggle('abc-has-badge', !!pill);
      let labelRow = priceContainer.querySelector(':scope > .abc-label-row');
      if (pill) {
        if (!labelRow) { labelRow = document.createElement('div'); labelRow.className = 'abc-label-row'; priceContainer.appendChild(labelRow); }
        if (labelRow.innerHTML !== pill) labelRow.innerHTML = pill;
      } else if (labelRow) {
        labelRow.remove();
      }
    }

    const cardLinks = productCard.querySelectorAll('[data-product-card-link], a.grid__image');
    cardLinks.forEach(link => {
      const url = link.getAttribute('href');
      if (url) {
        const base = url.split('?')[0];
        link.setAttribute('href', base + '?variant=' + variantId);
      }
    });

  }

  /* ---------------- UNIT PRICE (€/kg) ---------------- */

  if (unitPriceEl) {

    const unitPrice = pricePill.getAttribute('data-unit-price');
    const unitMeasurement = pricePill.getAttribute('data-unit-price-measurement');

    if (unitPrice && unitMeasurement) {
      // Scale the unit price (Grundpreis) by the test/original ratio so €/kg
      // stays consistent with the displayed test price.
      let unitPriceDisplay = unitPrice;
      if (abcPrice && abcPrice.price != null && abcPrice.price !== '') {
        const origNum = parseMoney(origPrice);
        if (origNum > 0) {
          const ratio = Number(abcPrice.price) / origNum;
          if (isFinite(ratio)) {
            unitPriceDisplay = formatLikeTemplate(unitPrice, parseMoney(unitPrice) * ratio);
          }
        }
      }
      unitPriceEl.textContent = `(${unitPriceDisplay} / ${unitMeasurement.replace(/^1/, '')})`;
    } else {
      unitPriceEl.textContent = '';
    }

  }

  /* ---------------- STOCK ---------------- */

  const inventory = parseInt(pill.getAttribute('data-inventory'), 10);

  if (!isNaN(inventory) && inventory > 0 && inventory < 10) {

    if (stockBox && stockQty) {
      stockQty.textContent = inventory;
      stockBox.style.display = 'block';
    }

  } else {

    if (stockBox) stockBox.style.display = 'none';

  }

}

    /* ---------------- UI ADJUST ---------------- */

    if (variantSelector) {
      variantSelector.classList.toggle('few-pills', pills.length < 4);
    }

    pills.forEach(pill => {
      pill.addEventListener('click', () => {
        if (!pill.classList.contains('disabled')) {
          activatePill(pill);
        }
      });
    });

    /* ---------------- AUTO SELECT ---------------- */

    const STANDARD_KEYWORDS = [
      'standard', 'regular',
      'standardgröße', 'standardgroesse',
      'estándar', 'estandar',
      'classique', 'basique',
      'taille standard', 'unique'
    ];

    function isStandardLabel(label) {
      if (!label) return false;
      const text = label.toLowerCase();
      return STANDARD_KEYWORDS.some(k => text.includes(k));
    }

    function runAutoSelect() {

      const forcedId = productCard.dataset.forcedVariantId;
      if (forcedId) {
        const forcedPill = Array.from(pills).find(p => p.dataset.variantId === forcedId);
        if (forcedPill) { activatePill(forcedPill); return; }
      }

      if (isCardPickerMode()) {
        const defaultId = productCard.dataset.defaultVariantId;
        const defaultPill = Array.from(pills).find((pill) => {
          const isAvailable = pill.getAttribute('data-available') === 'true';
          const isDisabled = pill.classList.contains('disabled');
          return pill.dataset.variantId === defaultId && isAvailable && !isDisabled;
        });
        if (defaultPill) { activatePill(defaultPill); return; }
      }

      let standardPill = null;

      pills.forEach(pill => {
        const label = pill.textContent?.trim();
        const isAvailable = pill.getAttribute('data-available') === 'true';
        const isDisabled = pill.classList.contains('disabled');
        const isVisible = getComputedStyle(pill).display !== 'none';

        if (isAvailable && !isDisabled && isVisible && isStandardLabel(label)) {
          standardPill = pill;
        }
      });

      if (standardPill) {
        activatePill(standardPill);
        return;
      }

      const firstAvailable = Array.from(pills).find(pill => {
        const isAvailable = pill.getAttribute('data-available') === 'true';
        const isDisabled = pill.classList.contains('disabled');
        const isVisible = getComputedStyle(pill).display !== 'none';
        return isAvailable && !isDisabled && isVisible;
      });

      if (firstAvailable) {
        activatePill(firstAvailable);
      }

    }

    // first select
    runAutoSelect();

    // Center the auto-selected pill inside its strip; the edge fades below
    // signal any clipped neighbours. Adjusting scrollLeft never moves the
    // page, unlike scrollIntoView.
    const centerActivePill = () => {
      const strip = variantSelector;
      const active = productCard.querySelector('.size-pill.active');
      if (!strip || !active || strip.scrollWidth <= strip.clientWidth) return;
      const delta = active.getBoundingClientRect().left - strip.getBoundingClientRect().left;
      strip.scrollLeft += delta - (strip.clientWidth - active.offsetWidth) / 2;
    };
    centerActivePill();

    // Edge fades: fade-left / fade-right classes drive mask-image fades in
    // custom.css. A fade only shows when content is actually clipped on
    // that side - none at the first/last pill, none when everything fits.
    const updatePillFades = () => {
      const strip = variantSelector;
      if (!strip) return;
      const maxScroll = strip.scrollWidth - strip.clientWidth;
      strip.classList.toggle('fade-left', maxScroll > 2 && strip.scrollLeft > 2);
      strip.classList.toggle('fade-right', maxScroll > 2 && strip.scrollLeft < maxScroll - 2);
    };
    if (variantSelector) {
      variantSelector.addEventListener('scroll', updatePillFades, { passive: true });
      window.addEventListener('resize', updatePillFades, { passive: true });
    }
    updatePillFades();

    // The product-card component upgrade can wipe the .unit-price text
    // shortly after our first fill (it races with the component bundle,
    // which loads independently). Re-fill once things settle; both calls
    // are cheap no-ops when the text survived. Re-center the pill too -
    // late-loading fonts can change the strip's widths.
    const refillUnitPrice = () => {
      const active = productCard.querySelector('.size-pill.active');
      const upEl = productCard.querySelector('.product-grid--price > .unit-price');
      if (active && upEl && !upEl.textContent) activatePill(active);
      centerActivePill();
      updatePillFades();
    };
    setTimeout(refillUnitPrice, 800);
    setTimeout(refillUnitPrice, 2000);

    // Re-render the price (not the selection) once ABConvert's API is ready,
    // in case its script initialises after the card has rendered.
    if (window.ABCONVERT_PRICE_SYNC_ENABLED !== false &&
        !(window.abconvert && window.abconvert.utils &&
          window.abconvert.utils.isReady && window.abconvert.utils.isReady())) {
      let abcTries = 0;
      const abcReadyTimer = setInterval(() => {
        const ready = window.abconvert && window.abconvert.utils &&
                      window.abconvert.utils.isReady && window.abconvert.utils.isReady();
        if (ready || abcTries++ > 25) {
          clearInterval(abcReadyTimer);
          if (ready) {
            const activePill = productCard.querySelector('.size-pill.active');
            if (activePill) activatePill(activePill);
          }
        }
      }, 200);
    }

    /* ---------------- AJAX ATC ---------------- */

    const addToCartBtn = form?.querySelector('.add-to-cart-btn');
    const iconBtn = productCard.querySelector('.card-atc-icon');
    const hasMultiplePills = pills.length > 1 && !productCard.dataset.forcedVariantId;

    function onCardAtcClick(btn) {
      if (isCardPickerMode() && hasMultiplePills) {
        openCardVariantPicker(productCard);
        return;
      }
      const variantId = variantInput?.value;
      if (!variantId) return;
      addVariantToCart(variantId, btn, iconBtn);
    }

    if (addToCartBtn && addToCartBtn.dataset.atcInit !== '1') {
      addToCartBtn.dataset.atcInit = '1';
      addToCartBtn.addEventListener('click', () => onCardAtcClick(addToCartBtn));
    }

    if (iconBtn && iconBtn.dataset.atcInit !== '1') {
      iconBtn.dataset.atcInit = '1';
      iconBtn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        onCardAtcClick(iconBtn);
      });
    }

  });
}

/* ---------------- CARD VARIANT PICKER ---------------- */

function buildPickerOption(pill) {
  const selected = pill.classList.contains('active');
  const disabled = pill.classList.contains('disabled');
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'card-variant-picker__option' + (selected ? ' is-selected' : '') + (disabled ? ' is-disabled' : '');
  btn.dataset.variantId = pill.dataset.variantId || '';
  btn.setAttribute('role', 'option');
  btn.setAttribute('aria-selected', selected ? 'true' : 'false');
  if (disabled) btn.setAttribute('aria-disabled', 'true');

  const start = document.createElement('span');
  start.className = 'card-variant-picker__option-start';

  const radio = document.createElement('span');
  radio.className = 'card-variant-picker__radio';
  radio.setAttribute('aria-hidden', 'true');

  const prices = document.createElement('span');
  prices.className = 'card-variant-picker__option-prices';
  const priceEl = document.createElement('span');
  priceEl.className = 'card-variant-picker__option-price';
  priceEl.textContent = pill.getAttribute('data-price') || '';
  prices.appendChild(priceEl);
  const compare = pill.getAttribute('data-compare-at-price') || '';
  if (compare) {
    const compareEl = document.createElement('span');
    compareEl.className = 'card-variant-picker__option-compare';
    compareEl.textContent = compare;
    prices.appendChild(compareEl);
  }
  const unitPrice = pill.getAttribute('data-unit-price') || '';
  const unitMeasurement = pill.getAttribute('data-unit-price-measurement') || '';
  if (unitPrice && unitMeasurement) {
    const unit = document.createElement('span');
    unit.className = 'card-variant-picker__option-unit unit-price';
    unit.textContent = '(' + unitPrice + ' / ' + unitMeasurement.replace(/^1/, '') + ')';
    prices.appendChild(unit);
  }
  const title = document.createElement('span');
  title.className = 'card-variant-picker__option-title';
  const nameEl = pill.querySelector('.pill-name');
  const qtyEl = pill.querySelector('.pill-qty');
  const name = (nameEl ? nameEl.textContent : pill.textContent).trim();
  const qtyText = qtyEl ? qtyEl.textContent.trim() : '';
  title.textContent = qtyText ? name + ' ' + qtyText : name;
  start.append(radio, title);

  btn.append(start, prices);
  return btn;
}

function syncPickerFromCard(dialog, productCard, pill) {
  const img = dialog.querySelector('[data-picker-image]');
  const variantSrc = pill && pill.getAttribute('data-variant-image');
  const cardImg = productCard.querySelector('[data-product-card-image]');
  if (img) {
    const src = (variantSrc && variantSrc !== 'null' && variantSrc !== '')
      ? variantSrc
      : (cardImg ? (cardImg.currentSrc || cardImg.src) : '');
    if (src) {
      img.src = src;
      img.hidden = false;
    } else {
      img.removeAttribute('src');
      img.hidden = true;
    }
  }

  const priceEl = dialog.querySelector('[data-picker-price]');
  const compareEl = dialog.querySelector('[data-picker-compare]');
  const unitEl = dialog.querySelector('[data-picker-unit]');
  if (priceEl) {
    priceEl.textContent = pill ? (pill.getAttribute('data-price') || '') : '';
  }
  if (compareEl) {
    const compare = pill ? pill.getAttribute('data-compare-at-price') : '';
    compareEl.textContent = compare || '';
    compareEl.hidden = !compare;
  }
  if (unitEl) {
    const unitPrice = pill ? pill.getAttribute('data-unit-price') : '';
    const unitMeasurement = pill ? pill.getAttribute('data-unit-price-measurement') : '';
    if (unitPrice && unitMeasurement) {
      unitEl.textContent = `(${unitPrice} / ${unitMeasurement.replace(/^1/, '')})`;
      unitEl.hidden = false;
    } else {
      unitEl.textContent = '';
      unitEl.hidden = true;
    }
  }

  const stock = dialog.querySelector('[data-picker-stock]');
  if (stock && pill) {
    const inventory = parseInt(pill.getAttribute('data-inventory'), 10);
    const qty = stock.querySelector('.stock-quantity');
    if (!isNaN(inventory) && inventory > 0 && inventory < 10) {
      if (qty) qty.textContent = inventory;
      stock.hidden = false;
    } else {
      stock.hidden = true;
    }
  }

  if (pill) dialog.dataset.variantId = pill.getAttribute('data-variant-id') || '';
}

function openCardVariantPicker(productCard) {
  const dialog = document.getElementById('CardVariantPicker');
  if (!dialog || typeof dialog.showModal !== 'function') return;

  const pills = [...productCard.querySelectorAll('.size-pill[data-variant-id]')];
  const active = productCard.querySelector('.size-pill.active') || pills[0];
  const title = productCard.dataset.productTitle
    || productCard.querySelector('.product-grid--title a')?.textContent?.trim()
    || '';
  const url = productCard.dataset.productUrl
    || productCard.querySelector('[data-product-card-link]')?.getAttribute('href')
    || '#';

  dialog.dataset.sourceProductId = productCard.dataset.productId || '';

  const titleEl = dialog.querySelector('[data-picker-title]');
  if (titleEl) titleEl.textContent = title;
  const link = dialog.querySelector('[data-picker-link]');
  if (link) link.setAttribute('href', url);

  const img = dialog.querySelector('[data-picker-image]');
  if (img) img.alt = title;

  const options = dialog.querySelector('[data-picker-options]');
  if (options) {
    options.replaceChildren();
    pills.forEach((pill) => {
      const btn = buildPickerOption(pill);
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-disabled')) return;
        pill.click();
        options.querySelectorAll('.card-variant-picker__option').forEach((el) => {
          el.classList.remove('is-selected');
          el.setAttribute('aria-selected', 'false');
        });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-selected', 'true');
        syncPickerFromCard(dialog, productCard, pill);
      });
      options.appendChild(btn);
    });
  }

  syncPickerFromCard(dialog, productCard, active);
  if (!dialog.open) dialog.showModal();
}

function initCardVariantPicker() {
  const dialog = document.getElementById('CardVariantPicker');
  if (!dialog || dialog.dataset.pickerInit === '1') return;
  dialog.dataset.pickerInit = '1';

  dialog.querySelectorAll('[data-picker-close]').forEach((btn) => {
    btn.addEventListener('click', () => dialog.close());
  });

  dialog.addEventListener('click', (event) => {
    const panel = dialog.querySelector('.card-variant-picker__panel');
    if (panel && !panel.contains(event.target)) dialog.close();
  });

  dialog.addEventListener('close', () => {
    const atcBtn = dialog.querySelector('[data-picker-atc]');
    setAtcVisualState(atcBtn, 'idle');
  });

  const atc = dialog.querySelector('[data-picker-atc]');
  if (atc) {
    atc.addEventListener('click', () => {
      const variantId = dialog.dataset.variantId;
      const productId = dialog.dataset.sourceProductId;
      const card = productId
        ? document.querySelector(`product-card[data-product-id="${productId}"]`)
        : null;
      const cardIcon = card ? card.querySelector('.card-atc-icon') : null;
      addVariantToCart(variantId, atc, cardIcon);
    });
  }
}

/* ---------------- INIT ---------------- */

// Initial load
window.addEventListener('load', () => {
  applyCardPickerAssignment();
  initCardVariantPicker();
  initVariantChangeForProductCard(document);
});
customElements.whenDefined('product-card').then(() => {
  applyCardPickerAssignment();
  initCardVariantPicker();
  initVariantChangeForProductCard(document);
});

if (window.CARD_PICKER_ABTEST && window.CARD_PICKER_ABTEST.enabled) {
  let pickerTries = 0;
  let wasPickerMode = isCardPickerMode();
  const pickerReadyTimer = setInterval(() => {
    applyCardPickerAssignment();
    if (isCardPickerMode() && !wasPickerMode) {
      wasPickerMode = true;
      document.querySelectorAll('product-card[data-default-variant-id]').forEach((card) => {
        const pill = card.querySelector(
          `.size-pill[data-variant-id="${card.dataset.defaultVariantId}"]`
        );
        if (pill && !pill.classList.contains('disabled')) pill.click();
      });
    }
    const ready = window.abconvert && window.abconvert.utils &&
                  window.abconvert.utils.isReady && window.abconvert.utils.isReady();
    if (ready || pickerTries++ > 25) clearInterval(pickerReadyTimer);
  }, 200);
}

/* ---------------- AJAX GRID ---------------- */

function reinitCollectionGrid() {
  const grid = document.getElementById('CollectionProductGrid');
  if (grid) initVariantChangeForProductCard(grid);
}

const collectionGrid = document.getElementById('CollectionProductGrid');

if (collectionGrid) {

  let timeout;

  const observer = new MutationObserver(() => {

    clearTimeout(timeout);

    timeout = setTimeout(() => {
      observer.disconnect();
      reinitCollectionGrid();
      observer.observe(collectionGrid, { childList: true });
    }, 50);

  });

  observer.observe(collectionGrid, { childList: true });
}

/* ---------------- SLIDER END-SNAP ----------------
   Keep breathing room after the last carousel card (slidesOffsetAfter).
   Swiper exposes its instance on <grid-slider>; sliders init lazily, so we
   scan for a while and re-check on section loads. */
(function () {
  function alignEndSnap(el) {
    const swiper = el.swiper;
    if (!swiper || el.dataset.endSnapAligned === '1') return false;

    const apply = () => {
      const snap = swiper.snapGrid;
      if (!snap || snap.length < 2) return;
      const step = snap[1] - snap[0];
      if (step <= 1) return;
      // raw end position without our own offset (last snap includes it)
      const current = swiper.params.slidesOffsetAfter || 0;
      const rawMax = snap[snap.length - 1] - current;
      if (rawMax <= 0) return;
      // exact breathing room after the last card at the end position
      // (matches the side padding the bleed removed): 60px desktop, 15px
      // phones. The left card may show partially there - same language as
      // every other scroll position.
      // theme mobile boundary: 767px (desktop = min-width 768) - keep in sync across custom JS
      const offset = window.matchMedia('(min-width: 768px)').matches ? 60 : 15;
      if (Math.abs(offset - current) < 1) return;
      swiper.params.slidesOffsetAfter = offset;
      swiper.update();
    };

    el.dataset.endSnapAligned = '1';
    apply();
    swiper.on('resize', apply);
    return true;
  }

  let tries = 0;
  function scan() {
    // the Swiper instance lives on the <grid-slider> custom element itself,
    // not on the .product-grid--slider-enabled wrapper
    const sliders = document.querySelectorAll('grid-slider');
    let pending = false;
    sliders.forEach((el) => {
      armSliderPillNoSwipe(el);
      if (el.dataset.endSnapAligned !== '1' && !alignEndSnap(el)) pending = true;
    });
    // deferred bundles init sliders late; keep scanning briefly
    if ((pending || tries === 0) && tries < 40) {
      tries += 1;
      setTimeout(scan, 500);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  document.addEventListener('shopify:section:load', () => { tries = 0; scan(); });
})();

/* ---------------- LATE-LOADED CARDS ---------------- */

/* Recommendation and cross-sell sections fetch their markup after window
   load, so their cards miss the initial init and their pills stay dead.
   Re-run init when that HTML lands; the per-card variantChangeInit flag
   keeps repeated calls from binding twice. */
(function () {
  const reinit = () => initVariantChangeForProductCard(document);

  /* Names taken from component-product-recommendations.js, which dispatches
     both on document. Do not guess these: the similar-looking
     'product-recommendations:loaded' is dispatched by nobody. */
  ['recommendations:loaded', 'theme:section:load'].forEach((evt) => {
    document.addEventListener(evt, reinit);
  });

  const targets = [
    'product-recommendations',
    'cart-recommendations',
    '.cross-sell',
    '.cross-sells-wrapper',
  ].flatMap((sel) => [...document.querySelectorAll(sel)]);

  targets.forEach((target) => {
    new MutationObserver((mutations) => {
      if (mutations.some((m) => m.addedNodes.length)) reinit();
    }).observe(target, { childList: true, subtree: true });
  });
})();
